/**
 * RecordImportModal — T12.G.2: import rekordów z CSV.
 *
 * Flow:
 *  1. Użytkownik wybiera plik CSV.
 *  2. Dry-run: POST /api/modules/:id/records/import?dryRun=true → podgląd błędów + liczba wierszy.
 *  3. Potwierdzenie → POST bez dryRun → batch insert.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, FileUp, Upload } from 'lucide-react';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { api } from '@/lib/api';

interface ImportError {
  row: number;
  field: string;
  message: string;
}

interface ImportResponse {
  rowCount: number;
  wouldInsert?: number;
  inserted?: number;
  errors: ImportError[];
}

interface RecordImportModalProps {
  moduleId: string;
  moduleSlug: string;
  onClose: () => void;
  onSuccess?: (inserted: number) => void;
}

export function RecordImportModal({
  moduleId,
  moduleSlug,
  onClose,
  onSuccess,
}: RecordImportModalProps) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dryResult, setDryResult] = useState<ImportResponse | null>(null);
  const [importDone, setImportDone] = useState(false);

  const dryMutation = useMutation({
    mutationFn: (f: File) => {
      const form = new FormData();
      form.append('file', f);
      return api
        .post<ImportResponse>(`/modules/${moduleId}/records/import?dryRun=true`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },
    onSuccess: (data) => {
      setDryResult(data);
    },
  });

  const importMutation = useMutation({
    mutationFn: (f: File) => {
      const form = new FormData();
      form.append('file', f);
      return api
        .post<ImportResponse>(`/modules/${moduleId}/records/import`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },
    onSuccess: (data) => {
      setImportDone(true);
      onSuccess?.(data.inserted ?? 0);
    },
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setDryResult(null);
    setImportDone(false);
    if (f) dryMutation.mutate(f);
  }

  const hasErrors = (dryResult?.errors.length ?? 0) > 0;
  const canImport = dryResult && !hasErrors && !importDone;

  // Walidacja pliku albo import w toku — tło i Esc nie zamykają okna (X i przycisk działają).
  const busy = dryMutation.isPending || importMutation.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <Upload className="h-5 w-5 text-accent-500" />
          {t('tenant.modules.records.importCsv')}
        </span>
      }
      size="md"
      testId="record-import-modal"
      closeOnBackdrop={!busy}
      closeOnEsc={!busy}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {importDone ? t('common.close') : t('common.cancel')}
          </Button>
          {!importDone && (
            <Button
              variant="primary"
              onClick={() => file && importMutation.mutate(file)}
              disabled={!canImport || importMutation.isPending}
              leftIcon={
                importMutation.isPending ? (
                  <Spinner className="h-4 w-4" />
                ) : (
                  <Upload className="h-4 w-4" />
                )
              }
            >
              {t('tenant.modules.records.importConfirm', { count: dryResult?.wouldInsert ?? 0 })}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {/* File picker */}
        <div>
          <p className="mb-2 text-sm text-text-secondary">
            {t('tenant.modules.records.importHint', { slug: moduleSlug })}
          </p>
          <label className="flex cursor-pointer items-center gap-3 rounded-lg border-2 border-dashed border-border-primary px-4 py-6 hover:border-accent-500 transition-colors">
            <FileUp className="h-6 w-6 shrink-0 text-accent-500" />
            <div>
              <p className="text-sm font-medium text-text-primary">
                {file ? file.name : t('tenant.modules.records.importChooseFile')}
              </p>
              {file && (
                <p className="text-xs text-text-muted">{(file.size / 1024).toFixed(1)} KB</p>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              className="sr-only"
              onChange={handleFileChange}
            />
          </label>
        </div>

        {/* Dry-run result */}
        {dryMutation.isPending && (
          <div className="flex items-center gap-2 text-sm text-text-secondary">
            <Spinner className="h-4 w-4" />
            {t('tenant.modules.records.importValidating')}
          </div>
        )}

        {dryResult && (
          <div
            className={`rounded-lg border p-4 ${hasErrors ? 'border-destructive/40 bg-destructive/10' : 'border-green-300 bg-green-50'}`}
          >
            <div className="flex items-start gap-2">
              {hasErrors ? (
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              ) : (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
              )}
              <div className="flex-1 text-sm">
                <p className="font-medium">
                  {hasErrors
                    ? t('tenant.modules.records.importHasErrors', {
                        count: dryResult.errors.length,
                      })
                    : t('tenant.modules.records.importReady', {
                        count: dryResult.wouldInsert ?? dryResult.rowCount,
                      })}
                </p>
                {hasErrors && (
                  <ul className="mt-2 max-h-40 overflow-y-auto space-y-1">
                    {dryResult.errors.slice(0, 20).map((err, i) => (
                      <li key={i} className="text-xs text-destructive">
                        {t('tenant.modules.records.importErrorRow', {
                          row: err.row,
                          field: err.field,
                          message: err.message,
                        })}
                      </li>
                    ))}
                    {dryResult.errors.length > 20 && (
                      <li className="text-xs text-destructive/70">
                        {t('tenant.modules.records.importMoreErrors', {
                          count: dryResult.errors.length - 20,
                        })}
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Import done */}
        {importDone && (
          <div className="rounded-lg border border-green-300 bg-green-50 p-4 text-sm text-green-700">
            <p className="flex items-center gap-2 font-medium">
              <CheckCircle2 className="h-4 w-4" />
              {t('tenant.modules.records.importSuccess', {
                count: importMutation.data?.inserted ?? 0,
              })}
            </p>
          </div>
        )}

        {importMutation.isError && (
          <p className="text-sm text-destructive">{t('common.saveError')}</p>
        )}
      </div>
    </Modal>
  );
}
